"use client";

import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import "./ClientModal.css";

import { FaRegUser, FaWhatsapp, FaRegBuilding } from "react-icons/fa";
import { MdOutlineEmail, MdOutlineAttachMoney, MdFamilyRestroom } from "react-icons/md";
import { RxInstagramLogo } from "react-icons/rx";
import { LuCalendarDays } from "react-icons/lu";
import { GiModernCity } from "react-icons/gi";
import { IoColorPaletteOutline } from "react-icons/io5";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { CiPhone } from "react-icons/ci";
import { REST_API_URL } from "../../../api/config";
import { buildHeaders } from "../../../api/buildHeaders";
import { PrimaryButton } from "../../../components/buttons/primaryButton/PrimaryButton.tsx";

const BASE_URL = REST_API_URL;

 

 

const getInitialFormValues = () => ({
  "Client name": "",
  "Email Address": "",
  Instagram: "",
  "Phone Number": "",
  "WhatsApp number": "",
  "Company name": "",
  "TAX ID": "",
  Country: "",
  "About Service Notes": "",
  "Other Notes": "",
});

export default function AddClientModal({ onClose, client, onUpdated }) {
  const { t } = useTranslation();
  const isEditing = Boolean(client?.id);
  const isSubmittingRef = useRef(false);
  const initializedClientIdRef = useRef(null);

  const now = new Date();

  const [familyStatus, setFamilyStatus] = useState("");
  const [hobbies, setHobbies] = useState([]);
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
  const [showHobbiesDropdown, setShowHobbiesDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [birthday, setBirthday] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [formValues, setFormValues] = useState(getInitialFormValues());
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const familyOptions = [
    { value: "Single", label: t("home.familySingle") },
    { value: "Married", label: t("home.familyMarried") },
    { value: "In a relationship", label: t("home.familyInRelationship") },
    { value: "Engaged", label: t("home.familyEngaged") },
    { value: "Separated", label: t("home.familySeparated") },
    { value: "Divorced", label: t("home.familyDivorced") },
  ];

  const hobbyOptions = [
    { value: "Reading", label: t("home.hobbyReading") },
    { value: "Series", label: t("home.hobbySeries") },
    { value: "Anime", label: t("home.hobbyAnime") },
    { value: "Festivals", label: t("home.hobbyFestivals") },
    { value: "Martial arts", label: t("home.hobbyMartialArts") },
    { value: "Cooking", label: t("home.hobbyCooking") },
    { value: "Mountains", label: t("home.hobbyMountains") },
    { value: "Healthy lifestyle", label: t("home.hobbyHealthyLifestyle") },
    { value: "Motos", label: t("home.hobbyMotos") },
    { value: "Fishing/Hunting", label: t("home.hobbyFishingHunting") },
    { value: "Gardening", label: t("home.hobbyGardening") },
    { value: "Traveling", label: t("home.hobbyTraveling") },
    { value: "Pets", label: t("home.hobbyPets") },
    { value: "Games", label: t("home.hobbyGames") },
    { value: "Museums", label: t("home.hobbyMuseums") },
    { value: "Photography", label: t("home.hobbyPhotography") },
    { value: "Business", label: t("home.hobbyBusiness") },
  ];

  const months = [
    t("home.monthJanuary"),
    t("home.monthFebruary"),
    t("home.monthMarch"),
    t("home.monthApril"),
    t("home.monthMay"),
    t("home.monthJune"),
    t("home.monthJuly"),
    t("home.monthAugust"),
    t("home.monthSeptember"),
    t("home.monthOctober"),
    t("home.monthNovember"),
    t("home.monthDecember"),
  ];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    if (!client?.id) {
      initializedClientIdRef.current = null;
      setFormValues(getInitialFormValues());
      setFormErrors({});
      setFamilyStatus("");
      setHobbies([]);
      setBirthday("");
      setYear(now.getFullYear());
      setMonth(now.getMonth());
      setSelectedDay(null);
      return;
    }

    if (initializedClientIdRef.current === client.id) {
      return;
    }

    initializedClientIdRef.current = client.id;

    setFormValues({
      "Client name": client.name || "",
      "Email Address": client.email || "",
      Instagram: client.instagram_username || "",
      "Phone Number": client.phone_number || "",
      "WhatsApp number": client.whatsapp_number || client.whatapp_number || "",
      "Company name": client.company_name || "",
      "TAX ID": client.tax_id || "",
      Country: client.country || "",
      "About Service Notes": client.about_service_notes || "",
      "Other Notes": client.other_notes || "",
    });

    setFormErrors({});
    setFamilyStatus(client.family_status || "");
    setHobbies(
      client.hobbies
        ? String(client.hobbies)
            .split(",")
            .map((h) => h.trim())
            .filter(Boolean)
        : []
    );

    if (client.birthday) {
      const [y, m, d] = String(client.birthday).split("-");
      const parsedYear = Number(y);
      const parsedMonth = Number(m) - 1;
      const parsedDay = Number(d);

      setBirthday(
        `${String(parsedDay).padStart(2, "0")}/${String(parsedMonth + 1).padStart(2, "0")}/${parsedYear}`
      );
      setYear(parsedYear);
      setMonth(parsedMonth);
      setSelectedDay(parsedDay);
    } else {
      setBirthday("");
      setYear(now.getFullYear());
      setMonth(now.getMonth());
      setSelectedDay(null);
    }
  }, [client?.id]);

  useEffect(() => {
    if (selectedDay && selectedDay > daysInMonth) {
      setSelectedDay(daysInMonth);
    }
  }, [selectedDay, daysInMonth]);

  const closeAllDropdowns = () => {
    setShowFamilyDropdown(false);
    setShowHobbiesDropdown(false);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const toggleFamilyDropdown = () => {
    setShowFamilyDropdown((prev) => !prev);
    setShowHobbiesDropdown(false);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const toggleHobbiesDropdown = () => {
    setShowHobbiesDropdown((prev) => !prev);
    setShowFamilyDropdown(false);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const toggleCalendar = () => {
    setShowCalendar((prev) => !prev);
    setShowFamilyDropdown(false);
    setShowHobbiesDropdown(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const handleSaveDate = () => {
    if (!selectedDay) {
      toast.error(t("home.toastSelectDayFirst"));
      return;
    }

    const formatted = `${String(selectedDay).padStart(2, "0")}/${String(
      month + 1
    ).padStart(2, "0")}/${year}`;

    setBirthday(formatted);
    setShowCalendar(false);
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
  };

  const handleInputChange = (label, value) => {
    let error = "";
    let newValue = value;

    if (label.includes("Phone") || label.includes("WhatsApp")) {
      if (/[a-zA-Z]/.test(value)) {
        error = t("home.errorNumbersOnly");
      }
      newValue = value.replace(/[^\d+()\-\s]/g, "");
    }

    if (label.includes("Email")) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        error = t("home.errorInvalidEmail");
      }
    }

    setFormValues((prev) => ({
      ...prev,
      [label]: newValue,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [label]: error,
    }));
  };

  const validateForm = () => {
    const requiredFields = ["Client name"];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formValues[field] || !String(formValues[field]).trim()) {
        newErrors[field] = t("home.errorRequiredField");
      }
    });

    if (
      formValues["Email Address"] &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues["Email Address"])
    ) {
      newErrors["Email Address"] = t("home.errorInvalidEmail");
    }

    if (
      formValues["Phone Number"] &&
      /[a-zA-Z]/.test(formValues["Phone Number"])
    ) {
      newErrors["Phone Number"] = t("home.errorNumbersOnly");
    }

    if (
      formValues["WhatsApp number"] &&
      /[a-zA-Z]/.test(formValues["WhatsApp number"])
    ) {
      newErrors["WhatsApp number"] = t("home.errorNumbersOnly");
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaving || isSubmittingRef.current) return;

    if (!validateForm()) {
      toast.error(t("home.toastValidationError"));
      return;
    }

    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) {
      toast.error(t("home.toastAuthError"));
      return;
    }

    isSubmittingRef.current = true;
    setIsSaving(true);

    const payload = {
      name: formValues["Client name"]?.trim() || "",
      email: formValues["Email Address"]?.trim() || "",
      instagram_username: formValues["Instagram"]?.trim() || "",
      phone_number: formValues["Phone Number"]?.trim() || "",
      whatsapp_number: formValues["WhatsApp number"]?.trim() || "",
      company_name: formValues["Company name"]?.trim() || "",
      tax_id: formValues["TAX ID"]?.trim() || "",
      birthday: birthday ? birthday.split("/").reverse().join("-") : null,
      country: formValues["Country"]?.trim() || "",
      family_status: familyStatus || "",
      hobbies: hobbies.join(", "),
      about_service_notes: formValues["About Service Notes"] || "",
      other_notes: formValues["Other Notes"] || "",
    };

    const headers = buildHeaders();

    const url = isEditing
      ? `${BASE_URL}/client/client-view/${client.id}/`
      : `${BASE_URL}/client/client-create/`;

    const method = isEditing ? "PATCH" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        const messages = [];

        if (data && typeof data === "object") {
          Object.entries(data).forEach(([key, val]) => {
            if (Array.isArray(val)) {
              val.forEach((msg) => messages.push(`${key}: ${msg}`));
            } else if (typeof val === "string") {
              messages.push(`${key}: ${val}`);
            }
          });
        }

        if (messages.length) {
          messages.forEach((msg) => toast.error(msg));
        } else {
          toast.error(`Error ${res.status}: Something went wrong`);
        }
        return;
      }

      if (!isEditing && typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "new_client_added");
      }

      toast.success(isEditing ? t("home.toastSuccessUpdated") : t("home.toastSuccessClientCreated"));

      if (typeof onUpdated === "function") {
        onUpdated();
      }

      onClose();
    } catch (err) {
      console.error("Client submit error:", err);
      toast.error(t("home.toastNetworkError"));
    } finally {
      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  };

  const currentFamilyOption = familyOptions.find(o => o.value === familyStatus);
  const displayFamilyStatus = currentFamilyOption ? currentFamilyOption.label : familyStatus;

  const displayHobbies = hobbies.map(h => {
    const found = hobbyOptions.find(o => o.value === h);
    return found ? found.label : h;
  }).join(", ");

  return (
    <div className="add-clients__overlay" onClick={closeAllDropdowns}>
      <div className="add-clients" onClick={(e) => e.stopPropagation()}>
        <div className="add-clients__header">
          <h2>{isEditing ? t("home.titleEditClient") : t("home.titleAddClient")}</h2>
          <PrimaryButton
            label="✕"
            onClick={onClose}
            type="button"
            variant="ghost"
            size="small"
            className="add-clients__close-btn"
          />
        </div>

        <form className="add-clients__form" onSubmit={handleSubmit}>
          <div className="add-clients__row">
            <InputField
              icon={<FaRegUser />}
              label={t("home.labelClientName")}
              placeholder={t("home.placeholderClientName")}
              value={formValues["Client name"] || ""}
              error={formErrors["Client name"]}
              onChange={(v) => handleInputChange("Client name", v)}
            />
            <InputField
              icon={<MdOutlineEmail />}
              label={t("home.labelEmailAddress")}
              placeholder={t("home.placeholderEmailAddress")}
              value={formValues["Email Address"] || ""}
              error={formErrors["Email Address"]}
              onChange={(v) => handleInputChange("Email Address", v)}
            />
          </div>

          <div className="add-clients__row">
            <InputField
              icon={<RxInstagramLogo />}
              label={t("home.labelInstagram")}
              placeholder={t("home.placeholderInstagram")}
              value={formValues["Instagram"] || ""}
              onChange={(v) => handleInputChange("Instagram", v)}
            />
            <InputField
              icon={<CiPhone />}
              label={t("phoneNumber")}
              placeholder={t("home.placeholderPhoneNumber")}
              value={formValues["Phone Number"] || ""}
              error={formErrors["Phone Number"]}
              onChange={(v) => handleInputChange("Phone Number", v)}
            />
          </div>

          <div className="add-clients__row">
            <div className="add-clients__input-field">
              <label>{t("home.labelBirthday")}</label>
              <div
                className="add-clients__input-box relative birthday_box"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCalendar();
                }}
              >
                <span className="add-clients__icon add-clients__icon_birthday">
                  <LuCalendarDays />
                  <IoIosArrowDown className="open_calendar_add_clients" />
                </span>

                <input
                  type="text"
                  readOnly
                  placeholder={t("home.placeholderBirthday")}
                  value={birthday}
                />

                {showCalendar && (
                  <div
                    className="add-clients__calendar-dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="add-clients__calendar-header">
                      <div className="add-clients__calendar-select">
                        <div className="calendar-select-group">
                          <PrimaryButton
                            type="button"
                            className="add-clients__calendar-btn"
                            onClick={() => {
                              setShowYearDropdown((prev) => !prev);
                              setShowMonthDropdown(false);
                            }}
                            label={year}
                            rightIcon={<IoIosArrowDown />}
                            variant="ghost"
                            size="small"
                          />

                          {showYearDropdown && (
                            <div className="calendar-select-dropdown">
                              {Array.from({ length: 126 }, (_, i) => 1900 + i).map((y) => (
                                <div
                                  key={y}
                                  className={`calendar-select-item ${y === year ? "selected" : ""}`}
                                  onClick={() => {
                                    setYear(y);
                                    setShowYearDropdown(false);
                                  }}
                                >
                                  {y}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="calendar-select-group">
                          <PrimaryButton
                            type="button"
                            className="add-clients__calendar-btn"
                            onClick={() => {
                              setShowMonthDropdown((prev) => !prev);
                              setShowYearDropdown(false);
                            }}
                            label={months[month]}
                            rightIcon={<IoIosArrowDown />}
                            variant="ghost"
                            size="small"
                          />

                          {showMonthDropdown && (
                            <div className="calendar-select-dropdown">
                              {months.map((m, i) => (
                                <div
                                  key={m}
                                  className={`calendar-select-item ${i === month ? "selected" : ""}`}
                                  onClick={() => {
                                    setMonth(i);
                                    setShowMonthDropdown(false);
                                  }}
                                >
                                  {m}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="add-clients__calendar-grid">
                      {days.map((day) => (
                        <div
                          key={day}
                          className={`add-clients__calendar-day ${selectedDay === day ? "selected" : ""}`}
                          onClick={() => setSelectedDay(day)}
                        >
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="add-clients__calendar-footer">
                      <PrimaryButton
                        type="button"
                        className="cancel"
                        onClick={() => {
                          setShowCalendar(false);
                          setShowYearDropdown(false);
                          setShowMonthDropdown(false);
                        }}
                        label={t("home.buttonCancel")}
                        variant="ghost"
                        size="small"
                      />
                      <PrimaryButton
                        type="button"
                        className="save"
                        onClick={handleSaveDate}
                        label={t("home.buttonSave")}
                        size="small"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <InputField
              icon={<GiModernCity />}
              label={t("home.labelCountry")}
              placeholder={t("home.placeholderCountry")}
              value={formValues["Country"] || ""}
              onChange={(v) => handleInputChange("Country", v)}
            />
          </div>

          <div className="add-clients__row">
            <InputField
              icon={<FaWhatsapp />}
              label={t("home.labelWhatsAppNumber")}
              placeholder={t("home.placeholderWhatsAppNumber")}
              value={formValues["WhatsApp number"] || ""}
              error={formErrors["WhatsApp number"]}
              onChange={(v) => handleInputChange("WhatsApp number", v)}
            />
            <InputField
              icon={<FaRegBuilding />}
              label={t("home.labelCompanyName")}
              placeholder={t("home.placeholderCompanyName")}
              value={formValues["Company name"] || ""}
              onChange={(v) => handleInputChange("Company name", v)}
            />
          </div>

          <div className="add-clients__row">
            <InputField
              icon={<MdOutlineAttachMoney />}
              label={t("home.labelTaxId")}
              placeholder={t("home.placeholderTaxId")}
              value={formValues["TAX ID"] || ""}
              onChange={(v) => handleInputChange("TAX ID", v)}
            />

            <div className="add-clients__input-field">
              <label>{t("home.labelFamilyStatus")}</label>
              <div className="add-clients__input-box relative">
                <span className="add-clients__icon">
                  <MdFamilyRestroom />
                </span>

                <div
                  className="add-clients__select-display"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFamilyDropdown();
                  }}
                >
                  {displayFamilyStatus || t("home.placeholderFamilyStatus")}
                  <span className="add-clients__arrow">
                    {showFamilyDropdown ? <IoIosArrowUp /> : <IoIosArrowDown />}
                  </span>
                </div>

                {showFamilyDropdown && (
                  <div className="add-clients__dropdown">
                    {familyOptions.map((opt) => (
                      <label key={opt.value} className="add-clients__option">
                        <span>{opt.label}</span>
                        <input
                          type="radio"
                          name="familyStatus"
                          className="family-status-input"
                          checked={familyStatus === opt.value}
                          onChange={() => {
                            setFamilyStatus(opt.value);
                            setShowFamilyDropdown(false);
                          }}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="add-clients__input-field add-clients__full">
            <label>{t("home.labelHobbies")}</label>
            <div className="add-clients__input-box relative">
              <span className="add-clients__icon">
                <IoColorPaletteOutline />
              </span>

              <div
                className="add-clients__select-display"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHobbiesDropdown();
                }}
              >
                {displayHobbies || t("home.placeholderHobbies")}
                <span className="add-clients__arrow">
                  {showHobbiesDropdown ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </span>
              </div>

              {showHobbiesDropdown && (
                <div className="add-clients__dropdown">
                  {hobbyOptions.map((opt) => (
                    <label key={opt.value} className="add-clients__option">
                      <span>{opt.label}</span>
                      <input
                        type="checkbox"
                        className="hobbies-checkbox"
                        checked={hobbies.includes(opt.value)}
                        onChange={() =>
                          setHobbies((prev) => {
                            if (prev.includes(opt.value)) {
                              return prev.filter((h) => h !== opt.value);
                            }
                            if (prev.length >= 3) {
                              toast.error(t("home.hobbiesMaxLimitError") || "Maximum 3 hobbies allowed");
                              return prev;
                            }
                            return [...prev, opt.value];
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="add-clients__input-field add-clients__full">
            <label>{t("home.labelAboutServiceNotes")}</label>
            <textarea
              placeholder={t("home.placeholderDescription")}
              value={formValues["About Service Notes"] || ""}
              onChange={(e) => handleInputChange("About Service Notes", e.target.value)}
            />
          </div>

          <div className="add-clients__input-field add-clients__full">
            <label>{t("home.labelOtherNotes")}</label>
            <textarea
              placeholder={t("home.placeholderDescription")}
              value={formValues["Other Notes"] || ""}
              onChange={(e) => handleInputChange("Other Notes", e.target.value)}
            />
          </div>

          <div className="add-clients__button-row">
            <PrimaryButton
              type="button"
              className="add-clients__cancel-btn"
              onClick={onClose}
              disabled={isSaving}
              label={t("home.buttonCancel")}
              variant="outline"
            />

            <PrimaryButton
              type="submit"
              className="add-clients__add-btn"
              disabled={isSaving}
              label={
                isSaving 
                  ? t("home.buttonProcessing") 
                  : isEditing 
                    ? t("home.buttonSaveChanges") 
                    : t("home.buttonAdd")
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
}

const InputField = ({
  icon,
  label,
  placeholder,
  value = "",
  onChange,
  error,
}) => (
  <div className="add-clients__input-field">
    <label>{label}</label>
    <div className={`add-clients__input-box ${error ? "error" : ""}`}>
      <span className="add-clients__icon">{icon}</span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
    {error ? <p className="input-error-text">{error}</p> : null}
  </div>
);